import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCheck, ChevronDown, ChevronRight, ImageOff } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function NeedsPricing() {
  const [rows, setRows] = useState([]);
  const [missingImgs, setMissingImgs] = useState([]);
  const [tab, setTab] = useState("pricing");
  const [groupBy, setGroupBy] = useState("category");
  const [sortLegacy, setSortLegacy] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulk, setBulk] = useState({ making_mode: "pct", making_value: "", purity: "" });
  const [preview, setPreview] = useState(null);
  const [edits, setEdits] = useState({});
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/import/needs-pricing");
    setRows(r.data.rows);
    const mi = await api.get("/admin/import/missing-images");
    setMissingImgs(mi.data.rows);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const groups = useMemo(() => {
    let list = [...rows];
    if (sortLegacy) list.sort((a, b) => b.legacy_price_max - a.legacy_price_max);
    if (groupBy === "none") return { "All products": list };
    const g = {};
    for (const r of list) {
      const key = groupBy === "purity" ? (r.purities.join(" / ") || r.purity || "No purity") : (r.category || "Uncategorised");
      (g[key] = g[key] || []).push(r);
    }
    return g;
  }, [rows, groupBy, sortLegacy]);

  const targetBody = () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length) return { product_ids: ids };
    const body = {};
    if (bulk.purity) body.purity = bulk.purity;
    return body;
  };

  const doPreview = async () => {
    if (!bulk.making_value) return toast.error("Enter a making charge value first");
    try {
      const r = await api.post("/admin/import/price-preview", {
        ...targetBody(), making_mode: bulk.making_mode, making_value: Number(bulk.making_value),
      });
      setPreview(r.data.rows);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doBulkApply = async () => {
    if (!bulk.making_value) return toast.error("Enter a making charge value first");
    setBusy(true);
    try {
      const r = await api.post("/admin/import/bulk-pricing", {
        ...targetBody(), making_mode: bulk.making_mode, making_value: Number(bulk.making_value),
        ...(bulk.purity ? { purity_set: "" } : {}),
      });
      toast.success(`Making charge applied to ${r.data.updated} products`);
      setPreview(null);
      setSelected({});
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveRow = async (row) => {
    const e = edits[row.id] || {};
    setBusy(true);
    try {
      const body = { variants: [] };
      if (e.making_mode || e.making_value) {
        for (const v of row.variants) {
          body.variants.push({ id: v.id, making_mode: e.making_mode || v.making_mode, ...(e.making_value ? { [e.making_mode === "flat" ? "flat_making" : "making_value"]: Number(e.making_value) } : {}) });
        }
      }
      for (const v of row.variants) {
        const vw = e[`w_${v.id}`];
        if (vw) {
          const ex = body.variants.find((x) => x.id === v.id);
          if (ex) ex.net_weight = Number(vw);
          else body.variants.push({ id: v.id, net_weight: Number(vw) });
        }
      }
      const r = await api.patch(`/admin/import/products/${row.id}/pricing`, body);
      setRows(rows.map((x) => (x.id === row.id ? r.data : x)).filter((x) => x.missing.length));
      setEdits({ ...edits, [row.id]: {} });
      toast.success(`${row.title} updated`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const publishAll = async () => {
    if (!window.confirm("Publish every product whose pricing data is now complete? Incomplete rows stay as drafts.")) return;
    try {
      const r = await api.post("/admin/import/publish-complete");
      toast.success(`${r.data.published} published · ${r.data.still_incomplete} still incomplete`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-5" data-testid="needs-pricing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Needs Pricing Data</h1>
          <p className="text-sm text-neutral-500">{rows.length} drafts waiting · fill making charge → price computes live → publish.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("pricing")} className={`px-3 py-2 text-xs rounded ${tab === "pricing" ? "bg-neutral-900 text-white" : "border"}`} data-testid="tab-pricing">Needs pricing ({rows.length})</button>
          <button onClick={() => setTab("images")} className={`px-3 py-2 text-xs rounded flex items-center gap-1 ${tab === "images" ? "bg-neutral-900 text-white" : "border"}`} data-testid="tab-missing-images"><ImageOff size={12} /> Missing images ({missingImgs.length})</button>
          <button onClick={publishAll} className="px-4 py-2 bg-brand text-white text-xs rounded font-medium flex items-center gap-1.5" data-testid="publish-all-btn"><CheckCheck size={13} /> Publish all complete</button>
        </div>
      </div>

      {tab === "images" && (
        <section className="bg-white border rounded p-4" data-testid="missing-images-view">
          <p className="text-xs text-neutral-500 mb-3">Products with no image can never be published. Attach images via the Media Library.</p>
          <table className="w-full text-xs"><tbody className="divide-y">
            {missingImgs.map((p) => (
              <tr key={p.id}><td className="py-2"><b>{p.title}</b><span className="text-neutral-400 font-mono ml-2">{p.slug}</span></td>
                <td className="text-neutral-500">{p.category}</td><td>{p.status}</td>
                <td><a href="/admin/media" className="text-brand hover:underline">Attach images →</a></td></tr>
            ))}
            {!missingImgs.length && <tr><td className="py-4 text-neutral-400">No imageless products.</td></tr>}
          </tbody></table>
        </section>
      )}

      {tab === "pricing" && (
        <>
          {/* bulk apply bar */}
          <section className="bg-white border rounded p-4 flex flex-wrap items-end gap-3 text-xs" data-testid="bulk-bar">
            <div>
              <label className="block text-neutral-500 mb-1">Group by</label>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="border rounded px-2 py-1.5" data-testid="group-by-select">
                <option value="category">Type / category</option><option value="purity">Purity</option><option value="none">None</option>
              </select>
            </div>
            <label className="flex items-center gap-1.5 pb-1.5"><input type="checkbox" checked={sortLegacy} onChange={(e) => setSortLegacy(e.target.checked)} data-testid="sort-legacy-toggle" /> Sort by legacy price</label>
            <div className="h-8 w-px bg-neutral-200" />
            <div>
              <label className="block text-neutral-500 mb-1">Making charge mode</label>
              <select value={bulk.making_mode} onChange={(e) => setBulk({ ...bulk, making_mode: e.target.value })} className="border rounded px-2 py-1.5" data-testid="bulk-mode-select">
                <option value="pct">% of metal value</option><option value="per_gram">₹ per gram</option><option value="flat">Flat ₹ per piece</option>
              </select>
            </div>
            <div>
              <label className="block text-neutral-500 mb-1">Value</label>
              <input type="number" value={bulk.making_value} onChange={(e) => setBulk({ ...bulk, making_value: e.target.value })} className="border rounded px-2 py-1.5 w-28" placeholder="e.g. 12" data-testid="bulk-value-input" />
            </div>
            <div>
              <label className="block text-neutral-500 mb-1">Only purity</label>
              <select value={bulk.purity} onChange={(e) => setBulk({ ...bulk, purity: e.target.value })} className="border rounded px-2 py-1.5" data-testid="bulk-purity-select">
                <option value="">All</option><option>9KT</option><option>14KT</option><option>18K</option><option>22K</option><option>SILVER925</option>
              </select>
            </div>
            <button onClick={doPreview} className="px-3 py-2 border rounded" data-testid="bulk-preview-btn">Preview prices</button>
            <button onClick={doBulkApply} disabled={busy} className="px-4 py-2 bg-neutral-900 text-white rounded font-medium" data-testid="bulk-apply-btn">
              Apply to {selCount ? `${selCount} selected` : bulk.purity ? `all ${bulk.purity}` : "ALL drafts"}
            </button>
          </section>

          {preview && (
            <section className="bg-amber-50 border border-amber-200 rounded p-3 text-xs" data-testid="bulk-preview-result">
              <b>Preview — {preview.length} products at {bulk.making_mode === "pct" ? `${bulk.making_value}%` : bulk.making_mode === "per_gram" ? `₹${bulk.making_value}/g` : `₹${bulk.making_value} flat`} making charge:</b>
              <div className="grid md:grid-cols-3 gap-x-6 mt-1.5 max-h-40 overflow-y-auto">
                {preview.slice(0, 60).map((p) => (
                  <div key={p.id} className="flex justify-between py-0.5">
                    <span className="truncate">{p.title}</span>
                    <span>{inr(p.computed_min)}{p.legacy_price_max ? <span className="text-neutral-400"> (was {inr(p.legacy_price_max)})</span> : ""}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {Object.entries(groups).map(([gname, grows]) => (
            <section key={gname} className="bg-white border rounded overflow-hidden">
              {groupBy !== "none" && <div className="px-4 py-2 bg-neutral-50 text-xs font-semibold text-neutral-600">{gname} <span className="font-normal text-neutral-400">({grows.length})</span></div>}
              <table className="w-full text-xs" data-testid={`group-${gname}`}>
                <thead className="text-neutral-500 border-b">
                  <tr>{["", "Product", "Missing", "Legacy price", "Computed today", "Making charge", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y">
                  {grows.map((row) => {
                    const e = edits[row.id] || {};
                    const isOpen = open[row.id];
                    return [
                      <tr key={row.id} className="align-top">
                        <td className="px-3 py-2.5"><input type="checkbox" checked={!!selected[row.id]} onChange={(ev) => setSelected({ ...selected, [row.id]: ev.target.checked })} data-testid={`select-${row.slug}`} /></td>
                        <td className="px-3 py-2.5">
                          <button className="text-left" onClick={() => setOpen({ ...open, [row.id]: !isOpen })}>
                            {isOpen ? <ChevronDown size={12} className="inline" /> : <ChevronRight size={12} className="inline" />} <b>{row.title}</b>
                          </button>
                          <div className="text-neutral-400 font-mono">{row.slug} · {row.variants.length} variants · {row.purities.join("/") || row.purity}</div>
                        </td>
                        <td className="px-3 py-2.5 text-red-700 max-w-44">{row.missing.slice(0, 2).join(", ")}{row.missing.length > 2 && ` +${row.missing.length - 2}`}</td>
                        <td className="px-3 py-2.5">
                          {row.legacy_price_max ? inr(row.legacy_price_max) : "—"}
                          {row.legacy_below_metal && <div className="text-red-600 font-semibold flex items-center gap-1"><AlertTriangle size={11} /> below metal value</div>}
                        </td>
                        <td className="px-3 py-2.5 font-medium" data-testid={`computed-${row.slug}`}>{inr(row.computed_min)}{row.computed_max !== row.computed_min && `–${inr(row.computed_max)}`}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <select value={e.making_mode || "pct"} onChange={(ev) => setEdits({ ...edits, [row.id]: { ...e, making_mode: ev.target.value } })} className="border rounded px-1 py-1 mr-1" data-testid={`mode-${row.slug}`}>
                            <option value="pct">%</option><option value="per_gram">₹/g</option><option value="flat">flat</option>
                          </select>
                          <input type="number" placeholder="value" value={e.making_value || ""} onChange={(ev) => setEdits({ ...edits, [row.id]: { ...e, making_value: ev.target.value } })} className="border rounded px-1.5 py-1 w-20" data-testid={`making-${row.slug}`} />
                        </td>
                        <td className="px-3 py-2.5"><button onClick={() => saveRow(row)} disabled={busy} className="px-2.5 py-1 bg-neutral-900 text-white rounded" data-testid={`save-${row.slug}`}>Save</button></td>
                      </tr>,
                      isOpen && (
                        <tr key={row.id + "-v"} className="bg-neutral-50/60">
                          <td colSpan={7} className="px-8 py-2">
                            <table className="w-full">
                              <tbody className="divide-y divide-neutral-100">
                                {row.variants.map((v) => (
                                  <tr key={v.id}>
                                    <td className="py-1 font-mono text-neutral-500">{v.sku}</td>
                                    <td>{v.purity}</td>
                                    <td>net wt <input type="number" step="0.001" defaultValue={v.net_weight} className="border rounded px-1 py-0.5 w-20 ml-1" data-testid={`weight-${v.sku}`}
                                      onChange={(ev) => setEdits({ ...edits, [row.id]: { ...(edits[row.id] || {}), [`w_${v.id}`]: ev.target.value } })} /> g</td>
                                    <td>legacy {v.legacy_shopify_price ? inr(v.legacy_shopify_price) : "—"}</td>
                                    <td className="font-medium">{inr(v.computed)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </section>
          ))}
          {!rows.length && <div className="bg-white border rounded p-8 text-center text-neutral-400 text-sm" data-testid="queue-empty">Nothing waiting for pricing data. 🎉</div>}
        </>
      )}
    </div>
  );
}
